// @flow

import {
  ActionCreator,
  buildCatchError,
  createReducer,
} from '../../../store/utils';
import {of} from 'rxjs';
import {ActionsObservable, ofType} from 'redux-observable';
import update from 'immutability-helper';
import {map, switchMap, concatMap, catchError} from 'rxjs/operators';

import {
  deleteSizeChartApi,
  endpoints,
  getProductPageSettingApi,
  saveProductPageSettingApi,
  saveSizeChartApi,
  editSizeChartApi,
} from './api';

import {uploadImageStoreApi} from '../../file-storage/api';
import {createMultiFieldFormData} from '../../../utils/form';
import {GetSessionSuccess, LoginSuccess} from '../../auth/duck';
import {buildRoute} from '../../../http';

import type {ApiError} from '../../../http/error';
import type {Action} from '../../../store/utils';
import type {SizeChartRequest} from './api';
import type {StoredFile} from '../../../entities';

export type ProductPageAction = Action & {
  sizeChartRequest?: SizeChartRequest,
};

export type SizeChart = {
  id: string,
  image1Id?: string,
  image2Id?: string,
  name: string,
  sizeChartType: 'IMAGE',
};

export const CTATypeObject = {
  TO_CHAT: 'TO_CHAT',
  TO_ORDER_REQUEST: 'TO_ORDER_REQUEST',
  TO_SHOPPING_CART: 'TO_SHOPPING_CART',
  // TO_BUY_NOW: 'TO_BUY_NOW',
};

export type CTAType =
  | 'TO_CHAT'
  | 'TO_ORDER_REQUEST'
  | 'TO_SHOPPING_CART'
  | 'TO_BUY_NOW';
export type StoreProductCtaSetting = {
  enabled: boolean,
  primaryProductCtaButtonType: CTAType,
  secondaryProductCtaButtonType: CTAType,
  type: string,
};

export type StoreSizeChartSetting = {
  enabled: boolean,
  sizeCharts: SizeChart[],
  type: string,
};

export type ProductPageSetting = {
  storeProductCtaSetting?: StoreProductCtaSetting,
  storeSizeChartSetting?: StoreSizeChartSetting,
};

export type ProductPageState = {
  productPageSetting?: ProductPageSetting,
  isFetchingProductPageSetting?: boolean,
  isSavingSizeChart?: boolean,
  saveSizeChartError?: string,
  saveSizeChartSuccess?: boolean,
  saveProductPageSettingSuccess?: boolean,
  isSavingProductPageSetting?: boolean,
  saveProductPageSettingError?: string,
};

export class FetchProductPageSetting extends ActionCreator<ProductPageAction> {}
export class FetchProductPageSettingSuccess extends ActionCreator<ProductPageAction> {}
export class FetchProductPageSettingError extends ActionCreator<ProductPageAction> {}

export class SaveProductPageSetting extends ActionCreator<ProductPageAction> {}
export class SaveProductPageSettingSuccess extends ActionCreator<ProductPageAction> {}
export class SaveProductPageSettingError extends ActionCreator<ProductPageAction> {}

export class SaveSizeChart extends ActionCreator<ProductPageAction> {}
export class SaveSizeChartSuccess extends ActionCreator<ProductPageAction> {}
export class SaveSizeChartError extends ActionCreator<ProductPageAction> {}

export class EditSizeChart extends ActionCreator<ProductPageAction> {}
export class EditSizeChartSuccess extends ActionCreator<ProductPageAction> {}
export class EditSizeChartError extends ActionCreator<ProductPageAction> {}

export class DeleteSizeChart extends ActionCreator<ProductPageAction> {}
export class DeleteSizeChartSuccess extends ActionCreator<ProductPageAction> {}
export class DeleteSizeChartError extends ActionCreator<ProductPageAction> {}

export const actions = {
  saveSizeChart: (payload: ProductPageAction) => new SaveSizeChart(payload),
  editSizeChart: (payload: ProductPageAction) => new EditSizeChart(payload),
  deleteSizeChart: (payload: ProductPageAction) => new DeleteSizeChart(payload),
  saveProductPageSetting: (payload: ProductPageAction) =>
    new SaveProductPageSetting(payload),
  // fetchSizeCharts: () => new FetchSizeCharts(),
};
export const initialState: ProductPageState = {
  isFetchingProductPageSetting: true,
};

export default createReducer<ProductPageState>(initialState, {
  [FetchProductPageSetting.type]: (
    state: ProductPageState,
    action: ProductPageAction
  ) => ({
    ...state,
    isFetchingProductPageSetting: true,
    fetchProductPageSetting: undefined,
  }),
  [FetchProductPageSettingSuccess.type]: (
    state: ProductPageState,
    action: ProductPageAction
  ) => ({
    ...state,
    isFetchingProductPageSetting: undefined,
    productPageSetting: action.data,
  }),
  [FetchProductPageSettingError.type]: (
    state: ProductPageState,
    action: ProductPageAction
  ) => ({
    ...state,
    isFetchingProductPageSetting: undefined,
    fetchProductPageSetting: action.error,
  }),
  [SaveProductPageSetting.type]: (
    state: ProductPageState,
    action: ProductPageAction
  ) => ({
    ...state,
    isSavingProductPageSetting: true,
    saveProductPageSettingError: undefined,
    saveProductPageSettingSuccess: undefined,
  }),
  [SaveProductPageSettingSuccess.type]: (
    state: ProductPageState,
    action: ProductPageAction
  ) => ({
    ...state,
    isSavingProductPageSetting: undefined,
    saveProductPageSettingError: undefined,
    saveProductPageSettingSuccess: true,
    productPageSetting: action.data,
  }),
  [SaveProductPageSettingError.type]: (
    state: ProductPageState,
    action: ProductPageAction
  ) => ({
    ...state,
    isSavingProductPageSetting: undefined,
    saveProductPageSettingError: action.error,
    saveProductPageSettingSuccess: undefined,
  }),
  [SaveSizeChart.type]: (
    state: ProductPageState,
    action: ProductPageAction
  ) => ({
    ...state,
    isSavingSizeChart: true,
    saveSizeChartSuccess: undefined,
    saveSizeChartError: undefined,
  }),
  [SaveSizeChartSuccess.type]: (
    state: ProductPageState,
    action: ProductPageAction
  ) => ({
    ...state,
    saveSizeChartSuccess: true,
    isSavingSizeChart: undefined,
    saveSizeChartError: undefined,
  }),
  [SaveSizeChartError.type]: (
    state: ProductPageState,
    action: ProductPageAction
  ) => ({
    ...state,
    saveSizeChartError: action.error,
    isSavingSizeChart: undefined,
    saveSizeChartSuccess: undefined,
  }),
  [EditSizeChart.type]: (
    state: ProductPageState,
    action: ProductPageAction
  ) => ({
    ...state,
    isSavingSizeChart: true,
    saveSizeChartSuccess: undefined,
    saveSizeChartError: undefined,
  }),
  [EditSizeChartSuccess.type]: (
    state: ProductPageState,
    action: ProductPageAction
  ) => ({
    ...state,
    saveSizeChartSuccess: true,
    isSavingSizeChart: undefined,
    saveSizeChartError: undefined,
  }),
  [EditSizeChartError.type]: (
    state: ProductPageState,
    action: ProductPageAction
  ) => ({
    ...state,
    saveSizeChartError: action.error,
    isSavingSizeChart: undefined,
    saveSizeChartSuccess: undefined,
  }),
  [DeleteSizeChart.type]: (
    state: ProductPageState,
    action: ProductPageAction
  ) => ({
    ...state,
    isSavingSizeChart: true,
    saveSizeChartError: undefined,
    saveSizeChartSuccess: undefined,
  }),
  [DeleteSizeChartSuccess.type]: (
    state: ProductPageState,
    action: ProductPageAction
  ) => ({
    ...state,
    saveSizeChartSuccess: true,
    isSavingSizeChart: undefined,
    saveSizeChartError: undefined,
  }),
  [DeleteSizeChartError.type]: (
    state: ProductPageState,
    action: ProductPageAction
  ) => ({
    ...state,
    isSavingSizeChart: undefined,
    saveSizeChartSuccess: undefined,
    saveSizeChartError: action.error,
  }),
});

export function getProductPageSettingEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(
      LoginSuccess.type,
      GetSessionSuccess.type,
      SaveSizeChartSuccess.type,
      DeleteSizeChartSuccess.type,
      EditSizeChartSuccess.type
    ),
    switchMap(() => {
      return getProductPageSettingApi().pipe(
        map(
          (data: ProductPageSetting) =>
            new FetchProductPageSettingSuccess({data})
        ),
        buildCatchError(FetchProductPageSettingError)
      );
    })
  );
}

export function saveProductPageSettingEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(SaveProductPageSetting.type),
    switchMap(({productPageSettingRequest}: ProductPageAction) =>
      saveProductPageSettingApi(productPageSettingRequest).pipe(
        map(
          (data: ProductPageSetting) =>
            new SaveProductPageSettingSuccess({data})
        ),
        buildCatchError(SaveProductPageSettingError)
      )
    )
  );
}

export function saveSizeChartEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(SaveSizeChart.type),
    switchMap(({sizeChartRequest}: ProductPageAction) => {
      const files = sizeChartRequest.files;
      if (files.length) {
        return uploadImageStoreApi(
          createMultiFieldFormData(files, undefined, {
            filePrefix: 'im/product-page',
          })
        ).pipe(
          map((imageFiles: StoredFile[]) => [
            imageFiles,
            sizeChartRequest,
            undefined,
          ]),
          catchError((error: ApiError) => [undefined, undefined, error])
        );
      } else return of([undefined, sizeChartRequest, undefined]);
    }),
    concatMap(
      ([
        files: StoredFile[] = [],
        sizeChartRequest: SizeChartRequest,
        ApiError: ApiError | typeof undefined,
      ]) => {
        if (ApiError) return of(new SaveSizeChartError({error: ApiError}));
        let request = update(sizeChartRequest, {
          $unset: ['files'],
        });

        files.forEach((f: StoredFile, idx: number) => {
          request[`image${idx + 1}Id`] = f.id;
        });

        return saveSizeChartApi(request).pipe(
          map(data => new SaveSizeChartSuccess({data: data})),
          buildCatchError(
            SaveSizeChartError,
            'Error al crear la guía de tallas'
          )
        );
      }
    )
  );
}

export function editSizeChartEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(EditSizeChart.type),
    switchMap(({sizeChartRequest}: ProductPageAction) => {
      const files = sizeChartRequest.files;
      if (files.length) {
        return uploadImageStoreApi(
          createMultiFieldFormData(files, undefined, {
            filePrefix: 'im/product-page',
          })
        ).pipe(
          map((imageFiles: StoredFile[]) => [
            imageFiles,
            sizeChartRequest,
            undefined,
          ]),
          catchError((error: ApiError) => [undefined, undefined, error])
        );
      } else return of([undefined, sizeChartRequest, undefined]);
    }),
    concatMap(
      ([files = [], sizeChartRequest, ApiError]: [
        StoredFile[],
        SizeChartRequest,
        ApiError | typeof undefined
      ]) => {
        const sizeCharId = sizeChartRequest.id;
        if (ApiError || !sizeCharId)
          return of(new EditSizeChartError({error: ApiError}));
        let request = update(sizeChartRequest, {
          $unset: ['files', 'id'],
        });

        files.forEach((f: StoredFile, idx: number) => {
          request[`image${idx + 1}Id`] = f.id;
        });

        if (files.length < 2) {
          request.image2Id = '';
        }

        return editSizeChartApi(request, {
          route: buildRoute(endpoints.DELETE_SIZE_CHART, [sizeCharId]),
        }).pipe(
          map(data => new EditSizeChartSuccess({data: data})),
          buildCatchError(
            EditSizeChartError,
            'Error al actualizar la guía de tallas'
          )
        );
      }
    )
  );
}

export function deleteSizeChartEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(DeleteSizeChart.type),
    switchMap(({sizeChartRequest}: ProductPageAction) => {
      return deleteSizeChartApi({
        route: buildRoute(endpoints.DELETE_SIZE_CHART, [sizeChartRequest.id]),
      }).pipe(
        map(() => new DeleteSizeChartSuccess()),
        buildCatchError(DeleteSizeChartError)
      );
    })
  );
}
